/* eslint-disable max-len */
import {
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    Injector,
    Input,
    Output,
    QueryList,
    Renderer2,
    ViewChildren
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { UXDesignerComponentDirective } from '@Shared/Directives/proprietary/ux-designer-component.directive';
import { SetDirty } from '@Core/models/proprietary/page/page.actions';
import { PageSection, PageState } from '@Core/models/proprietary/page/page.state';
import { AppService } from '@Core/services/proprietary/app.service';
import { ObjectRuleService } from '@Core/services/proprietary/object-rule/object-rule.service';
import {
    EvaluatedObjectRules,
    FormGroupMapping,
    FormValidatorDictionary
} from '@Core/services/proprietary/object-rule/object-rule.types';

export interface OnFormDataChange {
    /**
     * A callback method that is invoked when a data is changed on an edit panel form group
     * Used primarily to trigger updates back to state
     * @param formGroupName The name of the formGroup that had a value change (edit)
     * @param value The change data for the entire formGroup
     */
    onFormDataChange(formGroupName: string, value: any): void;
}

export interface OnFormDataLoad {
    /**
     * A callback method that is invoked when data is loaded into the form group
     * Data could have been loaded from the [item] input changes or when a formGroup
     * is subscribed directly to state.
     * The setFormData method will also trigger this callback.
     * Used primarily to when the component need to set a local variable when data
     * changes.
     * @param formGroupName The name of the formGroup that data was loaded into
     * @param value The change data for the entire formGroup
     */
    onFormDataLoad(formGroupName: string, value: any): void;
}

export interface OnApplyRules {
    /**
     * A callback method that is invoked after object rules have been applied
     * Used primarily add local rules to the form that can't be implemented in object rules
     */
    onApplyRules(): void;
}

export class EditPanelConfig {
    /**
     * Class used to define EditPanel settings (config property)
     * objectService: The objectService that proceeses this edit panel's data
     * formMapping: Maps a formGroup to the validation nodeType and optionally specifies
     * an observable to watch for form data (as a simple state selector OR full defined
     * observable)
     * currentItemSelector: An optional state selector to get the current item of this
     * type when the EditPanel is used as a single item Modal
     */
    objectService: any;
    formMapping: {
        formGroupName: string;
        nodeType: string;
        selector?: any;
        data$?: Observable<any>;
        updateMethod?: any;
    }[];
    currentItemSelector?: any;
}

@Directive()
export abstract class EditPanelV2Component {
    readOnlyMode: boolean = false;
    ignorePageState: boolean = false; //set to true in descendant to bypass automatic readonly setting based on Page State Service -- should be rare
    ignoreDirtyTracking: boolean = false; //set to true in descendant to bypass setting pageState.Dirty when anyFormChangeSub detects a change -- should be rare

    @Input()
    allowClose: boolean = false;

    @Output()
    onClose = new EventEmitter();

    @Input()
    set item(value: any) {
        this.setFormGroupData(this._config.formMapping[0].formGroupName, value);
    }

    @Input()
    set dynamicData(value: any) {
        if (value && value.modal && value.modal == true) {
            if (!this._config.currentItemSelector) {
                console.error(
                    'There is no state selector specified for modal edit panel mode.   Set "currentModalItemSelector" in constructor of descendant.'
                );
                return;
            }
            // subscribe to the selector that returns the current item when in modal mode
            if (this.currentModalItemSub) {
                this.currentModalItemSub.unsubscribe();
            }
            this.currentModalItemSub = this.store.select(this._config.currentItemSelector).subscribe((data) => {
                this.setFormGroupData(this._config.formMapping[0].formGroupName, data);
            });
        }
    }

    @ViewChildren(UXDesignerComponentDirective) uxdChildren: QueryList<UXDesignerComponentDirective>;

    @Select(PageState.currentSection)
    currentSection$: Observable<any>;
    currentSectionSub: Subscription;
    currentSection: PageSection = null;

    pageEventSub: Subscription;
    pageReadOnlyModeSub: Subscription;
    protected currentModalItemSub: Subscription;
    protected anyFormChangeSub: Subscription;
    protected formGroupChangeSub: Subscription[] = [];
    protected stateChangeSub: Subscription[] = [];

    evaluatedFormRules: EvaluatedObjectRules = {};
    form: UntypedFormGroup;

    protected _config: EditPanelConfig;
    private _formGroupMapping: FormGroupMapping[] = []; // first mapping is the primary item
    private _childLayout: any = null;
    private _baseChildLayout: any = {}; //only populated in designer mode
    private _designerSectionID: number = null;
    private _baseFieldValidators: { [key: string]: ValidatorFn };
    private _prevComponentConfigSignature: string = '';

    public componentName = 'UNKNOWN';

    public get formGroupMapping(): FormGroupMapping[] {
        return this._formGroupMapping;
    }

    public get baseChildLayout(): any {
        return this._baseChildLayout;
    }

    public get childLayout(): any {
        return this._childLayout;
    }

    store: Store;
    changeDet: ChangeDetectorRef;
    fb: UntypedFormBuilder;
    objectRuleService: ObjectRuleService;
    renderer2: Renderer2;
    app: AppService;

    constructor(private inj: Injector) {
        this.store = inj.get(Store);
        this.changeDet = inj.get(ChangeDetectorRef);
        this.fb = inj.get(UntypedFormBuilder);
        this.objectRuleService = inj.get(ObjectRuleService);
        this.renderer2 = inj.get(Renderer2);
        this.app = inj.get(AppService);

        // TODO:  Remove this check after all Edit Panel components are refactored
        if (this['applyRules']) {
            console.error(
                'applyRules is an OBSOLETE method for EditPanelComponent descendants.  Convert code in applyRules() of descendant to "object rules" or implement onApplyRules()'
            );
        }
    }

    ngAfterViewInit() {
        this.initializeLayout(); //this MUST be in ngAfterViewInit
    }

    set config(value: EditPanelConfig) {
        this._config = value;

        this._formGroupMapping = [];

        value.formMapping.forEach((mapping) => {
            this._formGroupMapping.push({ formGroup: mapping.formGroupName, nodeType: mapping.nodeType });

            if (mapping.selector && mapping.data$) {
                console.error(
                    `Error: 'selector' and 'data$' properties cannot both be declared on the same formMapping!  formGroupName=${mapping.formGroupName}`
                );
            }
            // setup the observable if the simple dataSelector parameter exists
            if (mapping.selector) {
                mapping.data$ = this.store.select(mapping.selector);
            }
        });
    }

    ngOnInit() {
        this.storeBaseFieldValidators();
        if (!this._config) {
            throw 'There is no configuration defined for this edit panel.   Set "config" in constructor of descendant.';
        }

        if (!this.ignorePageState) {
            //listen for page events
            this.pageEventSub = this.store.select(PageState.pageEvent).subscribe((x) => {
                if (x == 'HighlightErrors') {
                    this.highlightErrors();
                }
                if (x == 'Reset') {
                    this.reset();
                }
            });

            this.pageReadOnlyModeSub = this.store.select(PageState.readOnlyMode).subscribe((x) => {
                this.readonly(x);
            });
            this.anyFormChangeSub = this.form.valueChanges.subscribe((value) => {
                if (!this.ignoreDirtyTracking) {
                    this.store.dispatch(new SetDirty());
                }
            });
        } else {
            this.applyObjectRules(); //NEEDED????
        }

        // map form changes for all formgroup mappings
        let mapIndex = 0;
        this._config.formMapping.forEach((mapping) => {
            //subscribe to state changes and load data to form -- IF a subscription is provided
            this.stateChangeSub[mapIndex] = null;
            this.formGroupChangeSub[mapIndex] = null;

            // if formgroup has a data observable mapped to it then subscribe
            if (mapping.data$) {
                this.stateChangeSub[mapIndex] = mapping.data$.subscribe((data) => {
                    this.setFormGroupData(mapping.formGroupName, data);
                });
            }

            //if onFormDataChange callback is defined or updateMethod specified on formgroup mapping then subscribe to changes on formGroup
            if (this['onFormDataChange'] || mapping.updateMethod) {
                this.formGroupChangeSub[mapIndex] = this.form.controls[mapping.formGroupName].valueChanges.subscribe(
                    (value) => {
                        //Override to use the rawFormValue, otherwise disabled controls are not included.
                        value = this.form.getRawValue()[mapping.formGroupName];
                        if (mapping.updateMethod) {
                            mapping.updateMethod(value);
                        }
                        if (this['onFormDataChange']) {
                            this['onFormDataChange'](mapping.formGroupName, value);
                        }
                    }
                );
            }
            mapIndex++;
        });
    }

    //move into service or extend form directive
    disableControl(control: any, disableCondition: boolean = true) {
        if (typeof control === 'string' || control instanceof String) {
            //path to control passed in as a string -- otherwise the  control was passed in
            control = this.form.get(control.toString());
        }
        if (control && control != null) {
            if (this.readOnlyMode == true) {
                control.disable({ emitEvent: false });
            } else {
                disableCondition ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
            }
        }
    }

    ngOnDestroy() {
        if (this.currentSectionSub) {
            this.currentSectionSub.unsubscribe();
        }
        if (this.pageEventSub) {
            this.pageEventSub.unsubscribe();
        }
        if (this.anyFormChangeSub) {
            this.anyFormChangeSub.unsubscribe();
        }
        if (this.pageReadOnlyModeSub) {
            this.pageReadOnlyModeSub.unsubscribe();
        }
        if (this.currentModalItemSub) {
            this.currentModalItemSub.unsubscribe();
        }
        this.formGroupChangeSub.forEach((sub) => {
            if (sub) {
                sub.unsubscribe();
            }
        });
        this.stateChangeSub.forEach((sub) => {
            if (sub) {
                sub.unsubscribe();
            }
        });
    }

    setFormGroupData(formGroup: string, value: any) {
        //console.log(`setFormGroupData>>>> formGroup: ${formGroup}  `); //value: ${JSON.stringify(value)}
        if (value) {
            this.form.controls[formGroup].patchValue(value, { emitEvent: false });

            this.applyObjectRules();

            //invoke onFormDataLoad callback if defined in Inheritor
            if (this['onFormDataLoad']) {
                this['onFormDataLoad'](formGroup, value);
            }

            //ML - The following detect changes call was causing a ViewDestroyedError
            //     There might be a better way of detecting if the view is destroyed before
            //     calling change detection
            // this.changeDet.detectChanges();
            // >>>>>>> BELOW NEEDED <<<<<<<<
            if (!this.changeDet['destroyed']) {
                this.changeDet.detectChanges();
                // >>>>>>>>>><<<<<<<<<<<<<<<<<<<
                // }
            }
        }
    }

    onAdd() {
        //!!!DO NOT ADD CODE HERE -- ADD CODE IN THE INHERITOR!!!
        console.error('onAdd() method not implemented!  Implement in inheritor.');
    }

    onClone() {
        //!!!DO NOT ADD CODE HERE -- ADD CODE IN THE INHERITOR!!!
        console.error('onClone() method not implemented!  Implement in inheritor.');
    }

    onDelete() {
        //!!!DO NOT ADD CODE HERE -- ADD CODE IN THE INHERITOR!!!
        console.error('onDelete() method not implemented!  Implement in inheritor.');
    }

    onRestore() {
        //!!!DO NOT ADD CODE HERE -- ADD CODE IN THE INHERITOR!!!
        console.error('onRestore() method not implemented!  Implement in inheritor.');
    }

    onAddAvailable() {
        //!!!DO NOT ADD CODE HERE -- ADD CODE IN THE INHERITOR!!!
        console.error('onAddAvailable() method not implemented!  Implement in inheritor.');
    }

    //evaluate rules base on data and object rule service rules, then apply rules.
    private applyObjectRules() {
        //skip rules if readonly
        let formDisabled = false;
        if (this.readOnlyMode) {
            this.disableControl(this.form);
            formDisabled = true;
        }

        //If primary FormGroup on the form has an Edit_Status == "D" || Edit_Status == "X", the whole form is disabled.
        if (!formDisabled) {
            let primaryFormGroup = this._formGroupMapping[0].formGroup;
            let editStatus = this.getControlValue(primaryFormGroup + '.Edit_Status');
            if (editStatus > '') {
                this.disableControl(this.form, ['D', 'X'].includes(editStatus));
                if (['D', 'X'].includes(editStatus)) {
                    formDisabled = true;
                }
            } else {
                this.disableControl(this.form, false);
            }
        }

        //evaluate rules for this form
        //Still evaluate rules, even if whole form is disabled. Needed for hide rules.
        this.evaluatedFormRules = this.objectRuleService.evaluateFormRules(
            this.form,
            this._config.objectService,
            this._formGroupMapping
        );

        this.restoreBaseFieldValidators(); //needed to reset styling of removed "required" rules  -- only used in DesignerMode

        if (!formDisabled) {
            //Skip applying rules if form is disabled.
            for (var fieldPath in this.evaluatedFormRules) {
                // Add validators to form fields
                if (this.evaluatedFormRules[fieldPath].validationRules != null) {
                    let validators = [];
                    for (var error in this.evaluatedFormRules[fieldPath].validationRules) {
                        validators.push(this.evaluatedFormRules[fieldPath].validationRules[error].rule);
                    }
                    this.setControlValidatorRules(fieldPath, validators, false);
                }

                // Set disabled status on fields with disabled rules
                if (this.evaluatedFormRules[fieldPath].disable != null) {
                    this.disableControl(fieldPath, this.evaluatedFormRules[fieldPath].disable);
                }

                //Note: Hidden rules are handled by code within <ep-field>, <ep-section>, etc. components
            }

            //invoke onApplyRules callback if defined in Inheritor
            if (this['onApplyRules']) {
                this['onApplyRules']();
            }
        }
    }

    highlightErrors() {
        if (this.form) {
            this.markAsTouched(this.form);
            this.changeDet.detectChanges();
        }
    }

    markAsTouched(group: UntypedFormGroup | UntypedFormArray) {
        Object.keys(group.controls).map((field) => {
            const control = group.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.markAsTouched(control);
            }
        });
    }

    reset() {
        this.markAsUntouched(this.form);
        this.changeDet.detectChanges();
    }

    readonly(value: boolean) {
        this.readOnlyMode = value;
        this.applyObjectRules();

        if (!this.changeDet['destroyed']) {
            this.changeDet.detectChanges();
        }
    }

    getControl(path: string): AbstractControl {
        return this.form.get(path);
    }

    getControlValue(path: string): any {
        // if (path == 'JournalEntry.Submitted_By.Date') {
        //   let segment: string[] = path.split('.');
        //   let value = this.form.getRawValue();

        //   for (let i = 0; i < segment.length; i++) {
        //     value = value[segment[i]];
        //     if (value === 'undefined') {
        //       return null;
        //     }
        //     if (value == null) {
        //       return null;
        //     }
        //   }
        //   if (value != '') {
        //     debugger;
        //     return value;
        //   } else {
        //     return value;
        //   }
        // } else {
        let segment: string[] = path.split('.');
        let value = this.form.getRawValue();

        for (let i = 0; i < segment.length; i++) {
            value = value[segment[i]];
            if (value === 'undefined') {
                return null;
            }
            if (value == null) {
                return null;
            }
        }
        return value;
        // }
    }

    // gets the computed record description from evaluated rules  ("$Record" field "Desc" property)
    getRecordDesc(rootFormGroup: string, defaultDesc: string = '?') {
        let desc = defaultDesc;
        let path = rootFormGroup + '.$Record';
        if (this.evaluatedFormRules[path]) {
            if (this.evaluatedFormRules[path].desc > '') {
                desc = this.evaluatedFormRules[path].desc;
            }
        }
        return desc;
    }

    // set validators for a control
    setControlValidatorRules(control: UntypedFormControl | string, validators: any[], append: boolean = true) {
        if (typeof control === 'string' || control instanceof String) {
            //path to control passed in as a string -- otherwise the  control was passed in
            control = <UntypedFormControl>this.form.get(control.toString());
        }
        if (control && control != null) {
            let newValidators: any[] = [];
            if (append && control.validator) {
                newValidators = [control.validator];
            }
            validators.forEach((validator: string) => {
                let leftParenPos = validator.indexOf('(');
                let rightParenPos = validator.lastIndexOf(')');
                let functionName = validator;
                //let parm: any[] = [];
                let parm = null;
                if (leftParenPos >= 0) {
                    if (rightParenPos > 0) {
                        parm = functionName.substr(leftParenPos + 1, rightParenPos - leftParenPos - 1);
                    }
                    functionName = functionName.substr(0, leftParenPos);
                }
                if (parm != null) {
                    if (FormValidatorDictionary[functionName]) {
                        if (functionName == 'range') {
                            // range validator has two parameters
                            let parms = parm.split(',');
                            newValidators.push(FormValidatorDictionary[functionName](parms[0], parms[1]));
                        } else {
                            newValidators.push(FormValidatorDictionary[functionName](parm));
                        }
                    }
                } else {
                    if (FormValidatorDictionary[functionName]) {
                        newValidators.push(FormValidatorDictionary[functionName]);
                    }
                }
            });

            control.setValidators(newValidators);
            control.updateValueAndValidity({ emitEvent: false }); // needed?? may depend on if we load rules before data
        }
    }

    markAsUntouched(group: UntypedFormGroup | UntypedFormArray) {
        Object.keys(group.controls).map((field) => {
            const control = group.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsUntouched({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.markAsUntouched(control);
            }
        });
    }

    close() {
        this.onClose.emit();
    }

    formValidationErrors() {
        this.highlightErrors();
        if (this.form && this.form.invalid) {
            return true;
        }
        return false;
    }

    //Initialization of customized edit panel layout feature
    initializeLayout() {
        this.storeBaseChildLayout();

        this.currentSection = this.store.selectSnapshot(PageState.currentSection);

        if (
            this.currentSection &&
            this.currentSection.Ux_Config &&
            this.currentSection.Ux_Config[this.componentName] &&
            this.currentSection.Ux_Config[this.componentName].ChildLayout
        ) {
            this._childLayout = this.currentSection.Ux_Config[this.componentName].ChildLayout;
        }

        this.renderLayout(this._childLayout);

        if (this.app.DesignerMode) {
            if (this.currentSection) {
                this._designerSectionID = this.currentSection.Section_ID;
            }

            // if in DesignerMode setup a subscription to listen to changes in the UXD Layout
            this.currentSectionSub = this.currentSection$.subscribe((currentSection) => {
                let pendingDesignerRuleChange = this.store.selectSnapshot(PageState.pendingDesignerRuleChange);

                //if a rule change is in process do not apply rules yet.  After object rules are applied this subscription will fire again.
                if (pendingDesignerRuleChange) {
                    return;
                }
                if (currentSection && this._designerSectionID != currentSection.Section_ID) {
                    return; // stop if section id changes from initial load in afterViewInit
                }

                let newComponentConfigSignature = '{}';
                if (currentSection && currentSection.Ux_Config && currentSection.Ux_Config[this.componentName]) {
                    newComponentConfigSignature = JSON.stringify(currentSection.Ux_Config[this.componentName]);
                }

                if (this._prevComponentConfigSignature == newComponentConfigSignature) {
                    return; //skip refresh if component rule signature has not changed
                }
                this._prevComponentConfigSignature = newComponentConfigSignature;

                if (
                    currentSection &&
                    currentSection.Ux_Config &&
                    currentSection.Ux_Config[this.componentName] &&
                    currentSection.Ux_Config[this.componentName].ChildLayout
                ) {
                    this._childLayout = currentSection.Ux_Config[this.componentName].ChildLayout;
                    this.renderLayout(this._childLayout);
                } else {
                    //restore back to baseChildLayout
                    this.renderLayout(this._baseChildLayout);
                }
                this.applyObjectRules(); // refresh edit panel after new rules are applied
                this.changeDet.detectChanges(); // force screen update
            });
        }
    }

    //saves off base field validators when in UX Designer Mode.   Needed so required validators can be dropped and re-added when rules are changed
    //must be in oninit()
    storeBaseFieldValidators() {
        if (!this.app.DesignerMode) {
            return;
        }
        this._baseFieldValidators = {};
        this.recursiveStoreBaseValidators(this.form, null);
    }

    restoreBaseFieldValidators() {
        if (!this.app.DesignerMode) {
            return;
        }
        for (var controlPath in this._baseFieldValidators) {
            const control = <UntypedFormControl>this.form.get(controlPath);
            control.setValidators(this._baseFieldValidators[controlPath]);
            control.updateValueAndValidity({ emitEvent: false });
        }
    }

    recursiveStoreBaseValidators(formgroup: UntypedFormGroup | UntypedFormArray, parentPath) {
        for (var controlName in formgroup.controls) {
            let path = parentPath ? parentPath + '.' + controlName : controlName;
            const control = formgroup.controls[controlName] as UntypedFormControl | UntypedFormGroup | UntypedFormArray;
            if (control instanceof UntypedFormControl) {
                // save all controls regardless of whether they have validators
                this._baseFieldValidators[path] = formgroup.controls[controlName].validator;
            } else {
                this.recursiveStoreBaseValidators(control, path);
            }
        }
    }

    //saves off base (compiled) child layout structure before user defined layout changes are made.
    // must be called in ngAftreViewInit.  Base child layout only stored if in DesignerMode.
    // base child layout stored in this._baseChildLayout.    This value is used by the ux-container-component
    storeBaseChildLayout() {
        if (!this.app.DesignerMode) {
            return;
        }

        let layout = {}; //index of parent elements with children in array

        this.uxdChildren.forEach((x) => {
            if (x.parentUxdName !== 'none') {
                if (!layout[x.parentUxdName]) {
                    layout[x.parentUxdName] = [];
                }
                layout[x.parentUxdName].push(x.uxdName);
            }
        });

        this._baseChildLayout = layout;
    }

    //Modifies Edit Panel Layout (move children if parent different from base layout,  sets the child order)
    renderLayout(childLayout: any) {
        for (let parentName in childLayout) {
            if (!(parentName === 'none')) {
                let parentUxComponent = this.uxdChildren.find((x) => x.uxdName == parentName);
                if (!parentUxComponent) {
                    console.log('Parent Element Not Found -> ' + parentName);
                    continue;
                }
                let parentContainerNativeEl = parentUxComponent.element.nativeElement.querySelector(
                    parentUxComponent.contentSelector
                ); //<<<< STORE QUERY SELECTOR IN UXDESIGNER DIRECTIVE  -- 
                // Each container element (block, section, group) has a div inside that is the flexbox container
                let childArray = childLayout[parentName] || [];

                for (let i = 0; i < childArray.length; i++) {
                    let childElementName = childArray[i];
                    let childUxComponent: UXDesignerComponentDirective = this.uxdChildren.find(
                        (x) => x.uxdName == childElementName
                    );
                    if (childUxComponent) {
                        if (childUxComponent.element) {
                            //if parent not the same as base compiled parent then move element to new parent  -- always move in designer mode
                            if (childUxComponent.parentUxdName != parentName || this.app.DesignerMode) {
                                this.renderer2.appendChild(parentContainerNativeEl, childUxComponent.element.nativeElement);
                            }
                            // negative numbers used for order so that unordered elements will show up and the
                            //  end of the parent  -99990, -999980, -99970
                            this.renderer2.setStyle(
                                childUxComponent.element.nativeElement,
                                'order',
                                ((i + 1) * 10 - 10000).toString()
                            );
                        } else {
                            console.log(
                                'Child Element cannot be moved (this element is not handled by UX directive) -> ' + childElementName
                            );
                        }
                    } else {
                        console.log('Child Element Not Found -> ' + childElementName);
                    }
                }
            }
        }
    }
}
